/**
 * MinigameRanking - Zeigt das Endergebnis eines Minispiels
 *
 * Zeigt:
 * - Ranking aller Spieler
 * - Scores
 * - Erhaltene Belohnungen (Punkte, Münzen, Mult)
 */
import React, { useState, useEffect, useCallback } from 'react';
import './MinigameRanking.css';
import socketService from '../../socketService';
import soundService from '../../soundService';
import { MINIGAME_EVENTS } from '../../shared/core/minigameConstants';
import { getMinigameById } from '../../shared/entities/minigames';
import { MINIGAMES } from '../../config/animationTimings';

const RANKING_DISPLAY_DURATION = MINIGAMES?.rankingDisplayDuration || 5000;

export default function MinigameRanking({
  minigameId,
  rankings,
  myPlayerIndex,
  onContinue,
}) {
  const [visibleRows, setVisibleRows] = useState(0);
  const [showRewards, setShowRewards] = useState(false);
  const [canContinue, setCanContinue] = useState(false);

  // Get minigame definition
  const minigame = getMinigameById(minigameId);

  // TODO: Use for highlighting own ranking row
  // const myRanking = rankings?.find(r => r.playerIndex === myPlayerIndex);

  // Animate rows appearing
  useEffect(() => {
    if (!rankings?.length) return;

    // Play result sound
    soundService.play('minigame_result');

    // Animate rows one by one
    const rowTimer = setInterval(() => {
      setVisibleRows(prev => {
        if (prev >= rankings.length) {
          clearInterval(rowTimer);
          // Show rewards after all rows are visible
          setTimeout(() => {
            setShowRewards(true);
            soundService.play('reward_collect');
          }, 500);
          return prev;
        }
        // Play sound for each row
        if (prev < 3) {
          soundService.play('placement_reveal');
        }
        return prev + 1;
      });
    }, 400);

    return () => clearInterval(rowTimer);
  }, [rankings]);

  // Handle continue
  const handleContinue = useCallback(() => {
    if (!canContinue) return;

    // Notify server
    socketService.emit(MINIGAME_EVENTS.ACKNOWLEDGE);
    onContinue?.();
  }, [canContinue, onContinue]);

  // Enable continue after delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setCanContinue(true);
    }, 3000);

    // Auto-continue after full duration
    const autoTimer = setTimeout(() => {
      // Direct call instead of handleContinue to avoid dependency issues
      socketService.emit(MINIGAME_EVENTS.ACKNOWLEDGE);
      onContinue?.();
    }, RANKING_DISPLAY_DURATION);

    return () => {
      clearTimeout(timer);
      clearTimeout(autoTimer);
    };
  }, [onContinue]);

  // Get rank display
  const getRankDisplay = (rank) => {
    switch (rank) {
      case 1:
        return { emoji: '🥇', class: 'gold', label: '1. Platz' };
      case 2:
        return { emoji: '🥈', class: 'silver', label: '2. Platz' };
      case 3:
        return { emoji: '🥉', class: 'bronze', label: '3. Platz' };
      default:
        return { emoji: `${rank}.`, class: 'other', label: `${rank}. Platz` };
    }
  };

  // Format response time in hundredths of seconds
  const formatTime = (ms) => {
    if (ms === null || ms === undefined) {
      return '-';
    }
    // In Sekunden mit 2 Dezimalstellen (Hundertstel)
    const seconds = (ms / 1000).toFixed(2);
    return `${seconds}s`;
  };

  return (
    <div className="minigame-ranking">
      <div className="minigame-ranking-backdrop" />

      <div className="minigame-ranking-content">
        {/* Header */}
        <div className="minigame-ranking-header">
          <span className="ranking-header-icon">{minigame?.icon || '🎮'}</span>
          <h2 className="ranking-header-title">Ergebnisse</h2>
          <p className="ranking-header-subtitle">{minigame?.name || 'Minispiel'}</p>
        </div>

        {/* Ranking list */}
        <div className="minigame-ranking-list">
          {rankings?.map((entry, index) => {
            const rankDisplay = getRankDisplay(entry.rank);
            const isMe = entry.playerIndex === myPlayerIndex;
            const isVisible = index < visibleRows;

            return (
              <div
                key={entry.playerIndex}
                className={`ranking-row ${rankDisplay.class} ${isMe ? 'is-me' : ''} ${isVisible ? 'visible' : ''}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                {/* Rank badge */}
                <div className="ranking-rank">
                  <span className="rank-emoji">{rankDisplay.emoji}</span>
                </div>

                {/* Player info */}
                <div className="ranking-player">
                  <span className="player-name">{entry.playerName || `Spieler ${entry.playerIndex + 1}`}</span>
                </div>

                {/* Raw game score (points achieved in minigame) */}
                <div className="ranking-raw-score">
                  <span className="raw-score-value">
                    {entry.rawScore !== undefined ? entry.rawScore : formatTime(entry.responseTimeMs)}
                  </span>
                  {entry.rawScore !== undefined && (
                    <span className="raw-score-label">
                      {minigameId === 'buttonMasher' ? 'Klicks' : 'Punkte'}
                    </span>
                  )}
                </div>

                {/* Rewards (shown after animation) */}
                {showRewards && entry.rewards && (
                  <div className="ranking-rewards">
                    <span className={`reward reward-points ${entry.rewards.points === 0 ? 'zero' : ''}`}>
                      +{entry.rewards.points} ⭐
                    </span>
                    {entry.rewards.coins > 0 && (
                      <span className="reward reward-coins">
                        +{entry.rewards.coins} 💰
                      </span>
                    )}
                    {entry.rewards.mult > 0 && (
                      <span className="reward reward-mult">
                        +{entry.rewards.mult.toFixed(1)} ×
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Continue button */}
        <button
          className={`ranking-continue-btn ${canContinue ? 'enabled' : ''}`}
          onClick={handleContinue}
          disabled={!canContinue}
        >
          {canContinue ? 'Weiter' : 'Bitte warten...'}
        </button>
      </div>
    </div>
  );
}
